import { useEffect, useMemo, useState, useRef, useCallback, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import { adminApi } from '../services/adminApi'
import { adminAuth } from '../services/adminAuth'
import type { Survey } from '../types/survey'
import type { AdminSurveyStats, AdminResultItem } from '../types/admin'
import { exportSummaryPdf, exportFullReportPdf } from '../utils/exportPdf'
import '../styles/admin.css'
import { AnalyticsTabs } from '../components/AnalyticsTabs'

function formatDuration(ms: number): string {
  const totalSec = Math.round(ms / 1000)
  if (totalSec < 60) return `${totalSec}s`
  const min = Math.floor(totalSec / 60)
  const sec = totalSec % 60
  return sec > 0 ? `${min}m ${sec}s` : `${min}m`
}

function Sparkline({ data }: { data: number[] }) {
  if (data.length < 2) return null
  const max = Math.max(...data)
  const min = Math.min(...data)
  const range = max - min || 1
  const w = 80
  const h = 24
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w
    const y = h - ((v - min) / range) * (h - 4) - 2
    return `${x},${y}`
  })
  return (
    <svg width={w} height={h} className="sparkline-svg" viewBox={`0 0 ${w} ${h}`}>
      <polyline
        points={points.join(' ')}
        fill="none"
        stroke="#a855f7"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

const TIME_BUCKETS = [
  { label: 'Under 1 min', max: 60000 },
  { label: '1-3 min', max: 180000 },
  { label: '3-5 min', max: 300000 },
  { label: '5-10 min', max: 600000 },
  { label: '10+ min', max: Infinity },
]

const BUCKET_COLORS = ['#a855f7', '#8b5cf6', '#6366f1', '#ec4899', '#f472b6']

export function AdminDashboard() {
  const [surveys, setSurveys] = useState<Survey[]>([])
  const [selectedSurveyId, setSelectedSurveyId] = useState<string>('')
  const [stats, setStats] = useState<AdminSurveyStats | null>(null)
  const [results, setResults] = useState<AdminResultItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showShare, setShowShare] = useState(false)
  const [copyFeedback, setCopyFeedback] = useState(false)
  const [showExportMenu, setShowExportMenu] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [exporting, setExporting] = useState(false)
  const exportRef = useRef<HTMLDivElement>(null)
  const settingsRef = useRef<HTMLDivElement>(null)
  const [showPassword, setShowPassword] = useState(false)
  const [showQrModal, setShowQrModal] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null)
  const [selectedSurvey, setSelectedSurvey] = useState<Survey | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    let mounted = true

    adminApi
      .listSurveys()
      .then((list) => {
        if (!mounted) return
        setSurveys(list)
        if (list[0]?.id) setSelectedSurveyId(list[0].id)
      })
      .catch((err) => {
        if (mounted) setError(err instanceof Error ? err.message : 'Failed to load surveys')
      })
      .finally(() => {
        if (mounted) setLoading(false)
      })

    return () => {
      mounted = false
    }
  }, [])

  useEffect(() => {
    if (!selectedSurveyId) return

    Promise.all([
      adminApi.getStats(selectedSurveyId),
      adminApi.getResults(selectedSurveyId),
      adminApi.getSurvey(selectedSurveyId),
    ])
      .then(([nextStats, nextResults, nextSurvey]) => {
        setStats(nextStats)
        setResults(nextResults)
        setSelectedSurvey(nextSurvey)
        setError(null)
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load stats')
      })
  }, [selectedSurveyId])

  const trendData = useMemo(() => {
    if (stats?.responseRateOverTime?.length) return stats.responseRateOverTime
    const bucket = new Map<string, number>()
    for (const row of results) {
      const d = row.submittedAt ? new Date(row.submittedAt) : new Date()
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      bucket.set(key, (bucket.get(key) || 0) + 1)
    }
    return Array.from(bucket.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([date, count]) => ({ date, count }))
  }, [results, stats])

  const sparklineData = useMemo(() => {
    const last7 = trendData.slice(-7)
    return last7.map((d) => d.count)
  }, [trendData])

  const timeBucketData = useMemo(() => {
    const counts = TIME_BUCKETS.map(() => 0)
    for (const r of results) {
      const dur = r.durationMs
      if (dur == null || dur <= 0) continue
      for (let i = 0; i < TIME_BUCKETS.length; i++) {
        if (dur <= TIME_BUCKETS[i].max) {
          counts[i]++
          break
        }
      }
    }
    return TIME_BUCKETS.map((b, i) => ({ label: b.label, count: counts[i] }))
  }, [results])

  const maxBucketCount = useMemo(() => Math.max(...timeBucketData.map((b) => b.count), 1), [timeBucketData])
  const hasTimingData = timeBucketData.some((b) => b.count > 0)

  function handleLogout() {
    adminAuth.clearToken()
    navigate('/admin/login', { replace: true })
  }

  async function handleDeleteSurvey() {
    if (!selectedSurveyId) return
    if (!confirm(`Delete survey ${selectedSurveyId}? This cannot be undone.`)) return

    try {
      await adminApi.deleteSurvey(selectedSurveyId)
      const next = surveys.filter((s) => s.id !== selectedSurveyId)
      setSurveys(next)
      setSelectedSurveyId(next[0]?.id || '')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed')
    }
  }

  async function handleResetSurvey() {
    if (!selectedSurveyId) return
    if (!confirm(`Reset survey ${selectedSurveyId}? This will delete all responses and respondent registrations.`)) return

    try {
      await adminApi.resetSurvey(selectedSurveyId)
      const [nextStats, nextResults] = await Promise.all([
        adminApi.getStats(selectedSurveyId),
        adminApi.getResults(selectedSurveyId),
      ])
      setStats(nextStats)
      setResults(nextResults)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reset failed')
    }
  }

  async function handleToggleEnabled() {
    if (!selectedSurveyId || !selectedSurvey) return
    const newEnabled = selectedSurvey.enabled === false ? true : false
    try {
      const updated = await adminApi.updateSurvey(selectedSurveyId, { enabled: newEnabled })
      setSelectedSurvey(updated)
      setSurveys((prev) => prev.map((s) => (s.id === selectedSurveyId ? { ...s, enabled: newEnabled } : s)))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle survey')
    }
  }

  async function copySurveyLink() {
    if (!selectedSurveyId) return
    const link = adminApi.getSurveyLink(selectedSurveyId)
    await navigator.clipboard.writeText(link)
    setCopyFeedback(true)
    setTimeout(() => setCopyFeedback(false), 2000)
  }

  async function downloadQr() {
    if (!selectedSurveyId) return
    const url = adminApi.getQrPngUrl(selectedSurveyId)
    const res = await fetch(url)
    const blob = await res.blob()
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `survey-${selectedSurveyId}-qr.png`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  const questionLabels = useMemo(() => {
    const map = new Map<string, string>()
    if (!selectedSurvey?.definition) return map
    const pages = (selectedSurvey.definition as any).pages
    if (!Array.isArray(pages)) return map
    for (const page of pages) {
      for (const el of (page.elements || page.questions || [])) {
        if (el.name) map.set(el.name, el.title || el.name)
      }
    }
    return map
  }, [selectedSurvey])

  const ANSWER_COLORS = ['#a855f7', '#6366f1', '#3b82f6', '#ec4899', '#8b5cf6', '#f472b6', '#818cf8', '#c084fc']

  const RESPONSES_KEY = 'Responses'

  const { answerDistData, answerKeys } = useMemo(() => {
    const breakdowns = stats?.questionBreakdowns || []
    const allKeys = new Set<string>()
    const data = breakdowns.map((b) => {
      const label = questionLabels.get(b.questionId) || b.questionId
      const truncated = label.length > 30 ? label.slice(0, 27) + '...' : label
      const row: Record<string, string | number> = { question: truncated }
      if (b.type === 'text') {
        row[RESPONSES_KEY] = b.totalAnswers
        allKeys.add(RESPONSES_KEY)
      } else {
        for (const [key, count] of Object.entries(b.distribution)) {
          row[key] = count
          allKeys.add(key)
        }
      }
      return row
    })
    return { answerDistData: data, answerKeys: Array.from(allKeys) }
  }, [stats, questionLabels])

  const handleExport = useCallback(async (mode: 'summary' | 'full') => {
    if (!stats || !selectedSurveyId) return
    setShowExportMenu(false)
    setExporting(true)
    try {
      const opts = {
        stats,
        results,
        surveyTitle: selectedSurvey?.title || selectedSurveyId,
        surveyId: selectedSurveyId,
        questionLabels,
      }
      if (mode === 'summary') {
        await exportSummaryPdf(opts)
      } else {
        await exportFullReportPdf(opts)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'PDF export failed')
    } finally {
      setExporting(false)
    }
  }, [stats, results, selectedSurvey, selectedSurveyId, questionLabels])

  // Close export dropdown on outside click
  useEffect(() => {
    if (!showExportMenu) return
    function handleClick(e: MouseEvent) {
      if (exportRef.current && !exportRef.current.contains(e.target as Node)) {
        setShowExportMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [showExportMenu])

  // Close settings dropdown on outside click
  useEffect(() => {
    if (!showSettings) return
    function handleClick(e: MouseEvent) {
      if (settingsRef.current && !settingsRef.current.contains(e.target as Node)) {
        setShowSettings(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [showSettings])

  async function handleChangePassword(e: FormEvent) {
    e.preventDefault()
    setPasswordMessage(null)

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordMessage('All password fields are required.')
      return
    }
    if (newPassword !== confirmPassword) {
      setPasswordMessage('New password and confirmation do not match.')
      return
    }

    try {
      await adminApi.changePassword(currentPassword, newPassword)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setPasswordMessage('Password updated successfully.')
    } catch (err) {
      setPasswordMessage(err instanceof Error ? err.message : 'Failed to update password')
    }
  }

  if (loading) return <main className="admin-shell"><section className="glass-card">Loading dashboard...</section></main>

  return (
    <main className="admin-shell">
      <section className="glass-card">
        <div className="admin-header">
          <h1>Admin Dashboard</h1>
          <div className="settings-wrapper" ref={settingsRef}>
            <button className={`settings-gear-btn${showSettings ? ' settings-gear-open' : ''}`} onClick={() => setShowSettings(!showSettings)} aria-label="Settings">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
            </button>
            {showSettings && (
              <div className="settings-dropdown">
                <button className="settings-option settings-option-danger" onClick={() => { handleResetSurvey(); setShowSettings(false); }} disabled={!selectedSurveyId}>Reset Survey Data</button>
                <button className="settings-option settings-option-delete" onClick={() => { handleDeleteSurvey(); setShowSettings(false); }} disabled={!selectedSurveyId}>Delete Survey</button>
                <button className="settings-option" onClick={() => { setShowPassword(true); setShowSettings(false); }}>Change Password</button>
                <button className="settings-option" onClick={() => { handleLogout(); setShowSettings(false); }}>Log out</button>
              </div>
            )}
          </div>
        </div>

        <div className="admin-controls">
          <div className="admin-select-row">
            <select value={selectedSurveyId} onChange={(e) => setSelectedSurveyId(e.target.value)}>
              {surveys.map((survey) => (
                <option key={survey.id} value={survey.id}>
                  {survey.title} ({survey.id})
                </option>
              ))}
            </select>
            <a href={selectedSurveyId ? adminApi.getSurveyLink(selectedSurveyId) : '#'} target="_blank" rel="noreferrer"><button type="button" disabled={!selectedSurveyId}>Open Survey</button></a>
          </div>
          <div className="admin-buttons-row">
            <button onClick={() => navigate('/admin/builder')}>Create Survey</button>
            <button onClick={() => selectedSurveyId && navigate(`/admin/builder/${encodeURIComponent(selectedSurveyId)}`)} disabled={!selectedSurveyId}>Edit</button>
            <button onClick={() => setShowShare(!showShare)} disabled={!selectedSurveyId}>{showShare ? 'Hide Share' : 'Share'} <img src="/qr-icon.png" alt="" width="18" height="18" style={{verticalAlign: '-3px', marginLeft: '3px', filter: 'invert(1)'}} /></button>
            <div className="export-wrapper" ref={exportRef}>
              <button onClick={() => setShowExportMenu(!showExportMenu)} disabled={!stats || exporting}>
                {exporting ? 'Generating...' : 'Export to PDF'}
              </button>
              {showExportMenu && (
                <div className="export-dropdown">
                  <button className="export-option" onClick={() => handleExport('summary')}>Executive Summary</button>
                  <button className="export-option" onClick={() => handleExport('full')}>Full Report</button>
                </div>
              )}
            </div>
          </div>
        </div>

        {selectedSurvey && (
          <div className="survey-status-bar">
            <span className="badge badge-status">{selectedSurvey.status}</span>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={selectedSurvey.enabled !== false}
                onChange={handleToggleEnabled}
              />
              <span className="toggle-slider" />
              <span className="toggle-label">
                {selectedSurvey.enabled !== false ? 'Enabled' : 'Disabled'}
              </span>
            </label>
            {selectedSurvey.enabled === false && (
              <span className="badge badge-disabled">Disabled</span>
            )}
          </div>
        )}

        <div className={`share-panel ${showShare && selectedSurveyId ? 'open' : ''}`}>
          <div className="share-panel-inner">
            {selectedSurveyId && (
              <div className="share-card">
                <button className="share-dismiss" onClick={() => setShowShare(false)} aria-label="Close">&times;</button>
                <div className="share-content">
                  <div className="share-qr-section">
                    <h3>{selectedSurvey?.title || selectedSurveyId}</h3>
                    <div className="share-qr-wrap">
                      <img src={adminApi.getQrPngUrl(selectedSurveyId)} alt="Survey QR code" className="share-qr-img" onClick={() => setShowQrModal(true)} style={{cursor: 'pointer'}} />
                    </div>
                  </div>
                  <div className="share-actions-section">
                    <div className="share-link-row">
                      <code className="share-link-text">{adminApi.getSurveyLink(selectedSurveyId)}</code>
                    </div>
                    <button className="share-copy-btn" onClick={copySurveyLink}>{copyFeedback ? 'Copied!' : 'Copy Link'}</button>
                    <p className="share-qr-hint">
                      <span className="share-qr-hint-text">Click on the QR code to view a larger version to print</span>
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {error ? <p className="error-text">{error}</p> : null}

        {stats ? (
          <>
            <div className="metrics-grid">
              <article className="metric-card">
                <h3>Total Responses</h3>
                <p>{stats.totalResponses}</p>
                <Sparkline data={sparklineData} />
              </article>
              <article className="metric-card">
                <h3>Completed</h3>
                <p>{stats.completedResponses}</p>
                <Sparkline data={sparklineData} />
              </article>
              <article className="metric-card">
                <h3>Completion Rate</h3>
                <p>{stats.completionRate}%</p>
                <Sparkline data={sparklineData} />
              </article>
              <article className="metric-card">
                <h3>Avg. Completion Time</h3>
                <p>{stats.averageCompletionTimeMs ? formatDuration(stats.averageCompletionTimeMs) : '—'}</p>
              </article>
            </div>

            <div className="chart-card chart-card-full">
              <h3>Response Activity</h3>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={trendData} margin={{ left: 0, right: 10, top: 5, bottom: 5 }}>
                  <defs>
                    <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#a855f7" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#a855f7" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: '#94a3b8' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 11, fill: '#94a3b8' }}
                    axisLine={false}
                    tickLine={false}
                    width={30}
                  />
                  <Tooltip
                    contentStyle={{
                      background: 'rgba(15,23,42,0.9)',
                      border: '1px solid rgba(168,85,247,0.4)',
                      borderRadius: 8,
                      color: '#f8fafc',
                      fontSize: '0.85rem',
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="#a855f7"
                    strokeWidth={2}
                    fill="url(#areaGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="charts-row-2">
              <div className="chart-card answer-dist-card">
                <h3>Answer Distribution</h3>
                {answerDistData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={Math.max(answerDistData.length * 40 + 20, 120)}>
                    <BarChart data={answerDistData} layout="vertical" margin={{ left: 10, right: 10, top: 5, bottom: 5 }}>
                      <XAxis type="number" hide />
                      <YAxis
                        type="category"
                        dataKey="question"
                        width={120}
                        tick={{ fontSize: 11, fill: '#cbd5e1' }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip
                        contentStyle={{
                          background: 'rgba(15,23,42,0.9)',
                          border: '1px solid rgba(168,85,247,0.4)',
                          borderRadius: 8,
                          color: '#f8fafc',
                          fontSize: '0.85rem',
                        }}
                      />
                      {answerKeys.map((key, i) => (
                        <Bar key={key} dataKey={key} stackId="a" fill={ANSWER_COLORS[i % ANSWER_COLORS.length]} radius={i === answerKeys.length - 1 ? [0, 4, 4, 0] : undefined} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="analytics-empty">No answer data available</p>
                )}
              </div>

              <div className="chart-card histogram-card">
                <h3>Completion Time</h3>
                {hasTimingData ? (
                  <div className="time-histogram">
                    {timeBucketData.map((b, i) => (
                      <div key={b.label} className="histogram-row">
                        <span className="histogram-label">{b.label}</span>
                        <div className="histogram-bar-track">
                          <div
                            className="histogram-bar-fill"
                            style={{
                              width: `${(b.count / maxBucketCount) * 100}%`,
                              background: BUCKET_COLORS[i],
                            }}
                          />
                        </div>
                        <span className="histogram-count">{b.count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="analytics-empty">No timing data available</p>
                )}
              </div>
            </div>

            <AnalyticsTabs stats={stats} results={results} survey={selectedSurvey} />
          </>
        ) : null}

      </section>

      {showPassword ? (
        <div className="modal-backdrop" onClick={() => setShowPassword(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h2>Change Admin Password</h2>
            <form className="password-form" onSubmit={handleChangePassword}>
              <label>
                Current Password
                <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
              </label>
              <label>
                New Password
                <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={8} />
              </label>
              <label>
                Confirm New Password
                <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={8} />
              </label>
              <button type="submit">Update Password</button>
              {passwordMessage ? <p className="password-message">{passwordMessage}</p> : null}
            </form>
            <button onClick={() => setShowPassword(false)}>Close</button>
          </div>
        </div>
      ) : null}

      {showQrModal && selectedSurveyId ? (
        <div className="modal-backdrop qr-modal-backdrop" id="qr-modal-backdrop" onClick={() => setShowQrModal(false)}>
          <div className="modal-card qr-modal" onClick={(e) => e.stopPropagation()}>
            <button className="share-dismiss" onClick={() => setShowQrModal(false)} aria-label="Close">&times;</button>
            <div className="qr-modal-content">
              <div className="qr-modal-img-wrap">
                <img src={adminApi.getQrPngUrl(selectedSurveyId)} alt="Survey QR code" className="qr-modal-img" />
              </div>
              <h2 className="qr-modal-title">{selectedSurvey?.title || selectedSurveyId}</h2>
            </div>
            <button onClick={downloadQr}>Download QR (PNG)</button>
            <button onClick={() => window.print()}>Print</button>
          </div>
        </div>
      ) : null}

    </main>
  )
}
